import { formatAgo } from '@trips/shared/format';
import type { Source } from '@trips/shared/schema';
import { patch } from '../../data/mutate';
import { selectPrefs } from '../../data/selectors';
import { useStore } from '../../data/store';
import { deviceTimeZone } from '../../lib/platform';
import { navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { ListGroup, ListRow } from '../../ui/List';
import { Page } from '../../ui/Page';
import { IMPORT_ADDRESS } from '../settings/ClaudeImportPage';

const STATUS: Record<Source['status'], string> = {
  queued: 'Waiting for Claude',
  processing: 'Reading',
  added: 'Added',
  needs_review: 'Needs a look',
  needs_approval: 'Needs a look',
  failed: "Couldn't read",
  ignored: 'Ignored',
};

const done = (s: Source) => s.status === 'added' || s.status === 'ignored';

/** Forwarded bookings: what's waiting, what needs a look, and what was added. */
export function InboxScreen() {
  const sources = useStore((st) => st.tables.sources);
  const prefs = useStore(selectPrefs);
  const list = [...sources.values()]
    .filter((s) => !s.deleted && s.kind === 'email')
    .sort((a, b) => b.receivedAt - a.receivedAt);
  const waiting = list.filter((s) => !done(s));
  const handled = list.filter(done).slice(0, 30);
  const now = Date.now();
  const tz = deviceTimeZone();

  const row = (s: Source) => (
    <ListRow
      key={s.id}
      icon={s.status === 'failed' ? 'warning' : done(s) ? 'check-circle' : 'envelope-simple'}
      title={s.subject || '(no subject)'}
      detail={
        s.error ??
        s.summary ??
        [s.originalFrom ?? s.fromAddress, formatAgo(s.receivedAt, now, tz, prefs)]
          .filter(Boolean)
          .join(' · ')
      }
      value={STATUS[s.status]}
      trailing={
        !done(s) ? (
          <Button size="sm" onClick={() => void patch('sources', s.id, { status: 'ignored' })}>
            Ignore
          </Button>
        ) : undefined
      }
    />
  );

  if (list.length === 0) {
    return (
      <Page title="Inbox" backTo="/settings">
        <EmptyState
          icon="envelope-simple"
          title="Forward bookings here"
          action={
            <Button icon="envelope-simple" onClick={() => navigate('/settings/claude')}>
              Set up forwarding
            </Button>
          }
        >
          <p>
            Forward confirmation emails to {IMPORT_ADDRESS}. Each one shows up here, then becomes
            plans in the right trip.
          </p>
        </EmptyState>
      </Page>
    );
  }

  return (
    <Page title="Inbox" backTo="/settings">
      <ListGroup
        title="Waiting"
        footer={
          waiting.length
            ? 'Ask Claude to "import my trips inbox" to read these and add them.'
            : undefined
        }
      >
        {waiting.length ? waiting.map(row) : <ListRow title="Nothing waiting" />}
      </ListGroup>
      {handled.length > 0 && <ListGroup title="Done">{handled.map(row)}</ListGroup>}
    </Page>
  );
}
